import { createApp } from 'vue';

import { bootLoginPathForAuthDeniedReason } from 'boot/axiosAuthDeniedReason';
import { globalState, simpleObservableStore } from 'boot/simpleObservableStore';
import { createPinia } from 'pinia';
import App from 'src/App.vue';
import { bootAxios } from 'src/boot/axios';
import { bootAppearance } from 'src/boot/bootAppearance';
import { bootDevServiceWorkerCleanup } from 'src/boot/dev-service-worker-cleanup';
import { bootEventBus } from 'src/boot/eventBus';
import { bootI18n } from 'src/boot/i18n';
import { bootPreloadErrorRecovery } from 'src/boot/preloadErrorRecovery';
import { bootPrimeVue } from 'src/boot/primevue';
import { bootQueryStoreAndCommandBus } from 'src/boot/queryStoreAndCommandBus';
import { bootRxjsObservable } from 'src/boot/rxjs-observable';
import { bootSendIntent } from 'src/boot/send-intent';
import { bootServiceWorkerRegistration } from 'src/boot/serviceWorkerRegistration';
import { bootShortcuts } from 'src/boot/shortcuts';
import { bootTheming } from 'src/boot/theming';
import { bootVueZoomer } from 'src/boot/vue-zoomer';
import { bootVue3TouchEvents } from 'src/boot/vue3-touch-events';
import createAppRouter from 'src/router';

import SettingsGroupHeader from 'src/components/settings/SettingsGroupHeader.vue';

import { bridgeExternalOAuthAuthorizeRoute } from 'src/router/oauthAuthorizeBootstrap';

import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import 'src/boot/fontawesome-pro';
import 'src/css/app.scss';
import 'src/css/tailwind.css';

async function start(): Promise<void> {
	bridgeExternalOAuthAuthorizeRoute();

	const app = createApp(App);

	const pinia = createPinia();
	app.use(pinia);

	const router = createAppRouter();
	app.use(router);

	app.component('SettingsGroupHeader', SettingsGroupHeader);

	bootPreloadErrorRecovery();
	await bootDevServiceWorkerCleanup();
	await bootI18n({ app });
	bootPrimeVue({ app });
	bootEventBus({ app });
	bootAxios({ app });
	await bootQueryStoreAndCommandBus({ app });
	await bootTheming({ app });
	bootAppearance();
	bootVueZoomer({ app });
	bootVue3TouchEvents({ app });
	bootSendIntent({ app, router });
	bootRxjsObservable();
	bootServiceWorkerRegistration();

	if (globalState.eventBus !== null) {
		bootShortcuts({ eventBus: globalState.eventBus, router });
	}

	await router.isReady();

	const currentRoute = router.currentRoute.value;
	const authDeniedReason = simpleObservableStore.authDeniedReason;
	const isPublicOAuthEntry = currentRoute.name === 'OAuthAuthorizePage' || currentRoute.name === 'OAuthConsentPage';
	// A pending initial navigation can land after the 401 handler's #/login?reason=…, so re-add the reason here.
	const isLoginEntry =
		currentRoute.name === 'login' && (authDeniedReason === null || currentRoute.query.reason === authDeniedReason);
	if (simpleObservableStore.bootHealth === 'authLost' && !isPublicOAuthEntry && !isLoginEntry) {
		console.warn(`[boot] bootHealth=${simpleObservableStore.bootHealth} — redirecting to /login before mount`);
		await router.replace(bootLoginPathForAuthDeniedReason(authDeniedReason));
	}

	app.mount('#app');
}

void start();
